import React, { useEffect, useState } from "react";
import { HiArrowDownTray } from "react-icons/hi2";
import { usePublicPageSection } from "../../../../hooks/usePublicPageSection";
import { resolveMediaUrl } from "../../../../utils/mediaUrl";
import { useLanguage } from "../../../../context/LanguageContext";
import "./Steps.css";

const NAV_IDS = [
  "legal-framework",
  "whistleblower-protections",
  "rights-obligations",
];

const requireField = (fields, key) => {
  const value = fields?.[key];
  if (value == null || String(value).trim() === "") {
    throw new Error(`Law steps field "${key}" is missing from the API response.`);
  }
  return value;
};

const requireMediaUrl = (section, groupKey) => {
  const item = section.media?.groups?.[groupKey]?.[0] || null;
  if (!item?.url) {
    throw new Error(`Law steps media group "${groupKey}" is missing from the API response.`);
  }
  return resolveMediaUrl(item.url);
};

const buildModel = (section) => {
  const f = section.fields || {};
  return {
    navLabel: requireField(f, "navLabel"),
    navItems: [
      { id: NAV_IDS[0], label: requireField(f, "nav1Label") },
      { id: NAV_IDS[1], label: requireField(f, "nav2Label") },
      { id: NAV_IDS[2], label: requireField(f, "nav3Label") },
    ],
    downloadTitle: requireField(f, "downloadTitle"),
    downloadText: requireField(f, "downloadText"),
    downloadButton: requireField(f, "downloadButton"),
    downloadUrl: requireField(f, "downloadUrl"),
    downloadIcon: requireMediaUrl(section, "download"),
    frameworkIndex: requireField(f, "frameworkIndex"),
    frameworkHeading: requireField(f, "frameworkHeading"),
    frameworkIntro: requireField(f, "frameworkIntro"),
    frameworkSteps: [1, 2, 3, 4].map((n) => ({
      number: requireField(f, `step${n}Number`),
      title: requireField(f, `step${n}Title`),
      text: requireField(f, `step${n}Text`),
    })),
    protectionsIndex: requireField(f, "protectionsIndex"),
    protectionsHeading: requireField(f, "protectionsHeading"),
    protectionsIntro: requireField(f, "protectionsIntro"),
    protections: [1, 2, 3, 4].map((n) => ({
      title: requireField(f, `prot${n}Title`),
      text: requireField(f, `prot${n}Text`),
      icon: requireMediaUrl(section, `prot-${n}`),
    })),
    rightsIndex: requireField(f, "rightsIndex"),
    rightsHeading: requireField(f, "rightsHeading"),
    rightsIntro: requireField(f, "rightsIntro"),
    rightsListTitle: requireField(f, "rightsListTitle"),
    rights: [1, 2, 3, 4].map((n) => requireField(f, `right${n}`)),
    obligationsListTitle: requireField(f, "obligationsListTitle"),
    obligations: [1, 2, 3, 4].map((n) => requireField(f, `obligation${n}`)),
    checkIcon: requireMediaUrl(section, "check"),
  };
};

const Steps = () => {
  const { language, meta } = useLanguage();
  const { model, error, isLoading } = usePublicPageSection("law", "steps", buildModel);
  const [activeId, setActiveId] = useState(NAV_IDS[0]);

  useEffect(() => {
    if (model) setActiveId(NAV_IDS[0]);
  }, [language, model]);

  useEffect(() => {
    if (!model) return undefined;

    const sections = NAV_IDS.map((id) => document.getElementById(id)).filter(
      Boolean
    );

    if (!sections.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visible[0]?.target?.id) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        rootMargin: "-20% 0px -55% 0px",
        threshold: [0.1, 0.25, 0.5],
      }
    );

    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [model]);

  if (isLoading) {
    return (
      <section className="steps" dir={meta.dir}>
        <div className="steps__container">
          <p style={{ color: "#64748b", fontSize: 14 }}>Loading…</p>
        </div>
      </section>
    );
  }

  if (error || !model) {
    return (
      <section className="steps" dir={meta.dir}>
        <div className="steps__container">
          <p style={{ color: "#b91c1c", fontSize: 14 }}>
            {error || "Law steps unavailable."}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="steps" dir={meta.dir}>
      <div className="steps__container">
        <aside className="steps__sidebar">
          <div className="steps__sidebar-inner">
            <div className="steps__nav">
              <p className="steps__nav-label">{model.navLabel}</p>
              <ul className="steps__nav-list">
                {model.navItems.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className={`steps__nav-link ${
                        activeId === item.id ? "steps__nav-link--active" : ""
                      }`}
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="steps__download">
              <div className="steps__download-header">
                <img src={model.downloadIcon} alt="" aria-hidden="true" />
                <span>{model.downloadTitle}</span>
              </div>
              <p className="steps__download-text">{model.downloadText}</p>
              <a href={model.downloadUrl} className="steps__download-btn">
                <HiArrowDownTray />
                <span>{model.downloadButton}</span>
              </a>
            </div>
          </div>
        </aside>

        <div className="steps__content">
          <section id="legal-framework" className="steps__block">
            <span className="steps__index">{model.frameworkIndex}</span>
            <h2 className="steps__heading">{model.frameworkHeading}</h2>
            <p className="steps__intro">{model.frameworkIntro}</p>

            <ol className="steps__timeline">
              {model.frameworkSteps.map((step) => (
                <li key={step.number} className="steps__timeline-item">
                  <div className="steps__timeline-marker">
                    <span className="steps__timeline-number">{step.number}</span>
                  </div>
                  <div className="steps__timeline-body">
                    <h3 className="steps__timeline-title">{step.title}</h3>
                    <p className="steps__timeline-text">{step.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <section id="whistleblower-protections" className="steps__block">
            <span className="steps__index">{model.protectionsIndex}</span>
            <h2 className="steps__heading">{model.protectionsHeading}</h2>
            <p className="steps__intro">{model.protectionsIntro}</p>

            <div className="steps__cards">
              {model.protections.map((card) => (
                <article key={card.title} className="steps__card">
                  <div className="steps__card-icon">
                    <img src={card.icon} alt="" aria-hidden="true" />
                  </div>
                  <h3 className="steps__card-title">{card.title}</h3>
                  <p className="steps__card-text">{card.text}</p>
                </article>
              ))}
            </div>
          </section>

          <section id="rights-obligations" className="steps__block">
            <span className="steps__index">{model.rightsIndex}</span>
            <h2 className="steps__heading">{model.rightsHeading}</h2>
            <p className="steps__intro">{model.rightsIntro}</p>

            <div className="steps__lists">
              <div className="steps__list-col">
                <h3 className="steps__list-title">{model.rightsListTitle}</h3>
                <ul className="steps__list steps__list--rights">
                  {model.rights.map((item) => (
                    <li key={item}>
                      <img
                        src={model.checkIcon}
                        alt=""
                        className="steps__check"
                        aria-hidden="true"
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="steps__list-col">
                <h3 className="steps__list-title">{model.obligationsListTitle}</h3>
                <ul className="steps__list steps__list--obligations">
                  {model.obligations.map((item) => (
                    <li key={item}>
                      <span className="steps__bullet" aria-hidden="true" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        </div>
      </div>
    </section>
  );
};

export default Steps;
